<h2 mat-dialog-title>
  <span class="dialog-title">
    <span class="dialog-title-text">Resolver solicitud de desplazamiento</span>
    @if (context(); as ctx) {
      <span [class]="'status-badge status-badge--' + ctx.request.status">
        {{ EmployeeRequestResponsibleForAnswerStatusLabels[ctx.request.status] }}
      </span>
    }
  </span>
</h2>

<mat-dialog-content class="resolution-content">
  @if (loading()) {
    <div class="state-container" role="status" aria-live="polite">
      <mat-spinner diameter="40"></mat-spinner>
      <p>Cargando contexto de la solicitud…</p>
    </div>
  } @else {
    @if (load_error(); as error_message) {
      <div class="state-container state-container--error" role="alert">
        <mat-icon>error_outline</mat-icon>
        <p>{{ error_message }}</p>
        <button mat-stroked-button type="button" (click)="retryLoad()">Reintentar</button>
      </div>
    } @else {
      @if (context(); as ctx) {
        <section class="request-header" aria-label="Datos del desplazamiento">
          <div class="request-header-row">
            <img
              class="requester-avatar"
              [src]="ctx.request.requesting_user?.profile_picture_url || 'assets/img/default_profile_picture.png'"
              [alt]="ctx.request.requesting_user?.full_name ?? 'Solicitante desconocido'"
              onerror="this.src='assets/img/default_profile_picture.png'"
            />
            <div class="request-header-info">
              <p class="request-header-label">Propietario del evento</p>
              <p class="request-header-name">{{ ctx.request.requesting_user?.full_name ?? 'Empleado desconocido' }}</p>
              <p class="request-header-meta">
                <mat-icon class="icon-small" aria-hidden="true">schedule</mat-icon>
                {{ ctx.request.from_date | date: 'dd/MM/yyyy HH:mm' }} – {{ ctx.request.to_date | date: 'HH:mm' }}
              </p>
              @if (ctx.request.company_product_title || ctx.request.client_name) {
                <p class="request-header-meta">
                  <mat-icon class="icon-small" aria-hidden="true">business</mat-icon>
                  @if (ctx.request.company_product_title) {
                    {{ ctx.request.company_product_title }}
                  }
                  @if (ctx.request.client_name) {
                    · {{ ctx.request.client_name }}
                  }
                </p>
              }
              @if (ctx.request.origin_location || ctx.request.destination_location) {
                <p class="request-header-meta">
                  <mat-icon class="icon-small" aria-hidden="true">route</mat-icon>
                  {{ ctx.request.origin_location?.name ?? 'Origen desconocido' }} →
                  {{ ctx.request.destination_location?.name ?? 'Destino desconocido' }}
                  @if (ctx.request.trip_distance_km !== null) {
                    ({{ ctx.request.trip_distance_km }} km)
                  }
                </p>
              }
            </div>
          </div>
        </section>

        @if (!ctx.request.can_be_resolved) {
          <div class="state-banner state-banner--warning" role="status">
            <mat-icon aria-hidden="true">info</mat-icon>
            <span
              >Esta solicitud ya no está pendiente de resolución. Puedes consultar el contexto, pero no se puede modificar la decisión desde
              aquí.</span
            >
          </div>
        }

        <section class="decision-summary" aria-label="Resumen de la decisión">
          <div class="summary-stat">
            <span class="summary-stat-value">{{ ctx.candidates.length }}</span>
            <span class="summary-stat-label">candidato{{ ctx.candidates.length === 1 ? '' : 's' }}</span>
          </div>
          @if (ctx.voting_summary; as voting_summary) {
            <div class="summary-stat">
              <span class="summary-stat-value">{{ voting_summary.total_answering_users }}</span>
              <span class="summary-stat-label">asistente{{ voting_summary.total_answering_users === 1 ? '' : 's' }} que respondió</span>
            </div>
          }
          <div class="summary-chips">
            @if (voting_summary_chip(); as chip) {
              <span [class]="'summary-chip summary-chip--' + chip.tone">{{ chip.label }}</span>
            }
            @for (chip of evidence_summary_chips(); track chip.label) {
              <span [class]="'summary-chip summary-chip--' + chip.tone">{{ chip.label }}</span>
            }
          </div>
        </section>

        <section class="candidates-section" aria-labelledby="candidates-heading">
          <h3 id="candidates-heading">¿Quién condujo?</h3>

          @switch (scenario()) {
            @case ('no_candidates') {
              <div class="empty-state">
                <mat-icon aria-hidden="true">person_off</mat-icon>
                <p>No hay candidatos a conductor registrados para este desplazamiento.</p>
              </div>
            }
            @default {
              @if (scenario() === 'individual') {
                <p class="scenario-note">
                  <mat-icon class="icon-small" aria-hidden="true">info</mat-icon>
                  No existen votos porque el desplazamiento se realizó en solitario o no hubo más asistentes que pudieran identificar al
                  conductor. Revisa las evidencias para confirmar la decisión.
                </p>
              }

              <mat-radio-group
                class="candidates-grid"
                aria-label="Selecciona el conductor definitivo"
                [attr.aria-describedby]="show_driver_selection_hint() ? 'driver-selection-hint' : null"
                [formControl]="trip_driver_user_id"
              >
                @for (candidate of candidates(); track candidate.user_id) {
                  <mat-radio-button
                    [value]="candidate.user_id"
                    class="candidate-card"
                    [class.candidate-card--selected]="selected_driver_id() === candidate.user_id"
                  >
                    <span class="candidate-card-content">
                      <img
                        class="candidate-avatar"
                        [src]="candidate.profile_picture_url || 'assets/img/default_profile_picture.png'"
                        [alt]="candidate.full_name"
                        onerror="this.src='assets/img/default_profile_picture.png'"
                      />
                      <span class="candidate-info">
                        <span class="candidate-name">
                          {{ candidate.full_name }}
                          @if (!candidate.is_active_worker) {
                            <span class="candidate-inactive-badge" matTooltip="Este candidato ya no es un trabajador activo">Inactivo</span>
                          }
                        </span>

                        @if (scenario() === 'voting') {
                          <span class="candidate-votes">
                            {{ candidate.votes_count }} voto{{ candidate.votes_count === 1 ? '' : 's' }} · {{ candidate.vote_percentage }}%
                            @if (candidate.is_most_voted) {
                              <span class="most-voted-badge">
                                <mat-icon class="icon-small" aria-hidden="true">star</mat-icon>
                                Más votado
                              </span>
                            }
                          </span>
                          @if (candidate.voters.length > 0) {
                            <span class="candidate-voters">Votado por: {{ joinUserNames(candidate.voters) }}</span>
                          }
                        }

                        <span class="candidate-evidence-count">
                          <mat-icon class="icon-small" aria-hidden="true">fact_check</mat-icon>
                          {{ candidate.evidence_count }} evidencia{{ candidate.evidence_count === 1 ? '' : 's' }} compatible{{
                            candidate.evidence_count === 1 ? '' : 's'
                          }}
                        </span>
                      </span>
                    </span>
                  </mat-radio-button>
                }
              </mat-radio-group>

              @if (show_driver_selection_hint()) {
                <p id="driver-selection-hint" class="field-hint">Selecciona un candidato para poder aceptar la solicitud.</p>
              }
            }
          }
        </section>

        <section class="evidence-section" aria-label="Evidencias del desplazamiento">
          <h3>Evidencias</h3>

          <mat-accordion class="evidence-accordion" multi>
            <mat-expansion-panel>
              <mat-expansion-panel-header>
                <mat-panel-title>Fichajes</mat-panel-title>
                <mat-panel-description
                  >{{ ctx.evidences.clock_registers.length }} fichaje{{
                    ctx.evidences.clock_registers.length === 1 ? '' : 's'
                  }}</mat-panel-description
                >
              </mat-expansion-panel-header>

              @if (ctx.evidences.clock_registers.length === 0) {
                <p class="empty-state-inline">No se han encontrado fichajes relacionados con este desplazamiento.</p>
              } @else {
                <ul class="evidence-list">
                  @for (evidence of ctx.evidences.clock_registers; track evidence.id) {
                    <li class="evidence-item">
                      <img
                        class="evidence-avatar"
                        [src]="evidence.user.profile_picture_url || 'assets/img/default_profile_picture.png'"
                        [alt]="evidence.user.full_name"
                        onerror="this.src='assets/img/default_profile_picture.png'"
                      />
                      <div class="evidence-item-info">
                        <p class="evidence-item-title">
                          {{ evidence.user.full_name }} · {{ getClockRegisterTypeLabel(evidence.type) }} ·
                          {{ evidence.created_at | date: 'dd/MM HH:mm' }}
                        </p>
                        <p class="evidence-item-detail">{{ evidence.location_name ?? 'Ubicación no reconocida' }}</p>
                        <p class="evidence-item-detail">
                          Al origen: {{ formatDistanceMeters(evidence.distance_to_origin_meters) }} · Al destino:
                          {{ formatDistanceMeters(evidence.distance_to_destination_meters) }}
                        </p>
                      </div>
                      <span [class]="'relation-badge ' + getEvidenceRelationVisual(evidence.relation).cssClass">
                        <mat-icon class="icon-small" aria-hidden="true">{{ getEvidenceRelationVisual(evidence.relation).icon }}</mat-icon>
                        {{ getEvidenceRelationVisual(evidence.relation).label }}
                      </span>
                    </li>
                  }
                </ul>
              }
            </mat-expansion-panel>

            <mat-expansion-panel>
              <mat-expansion-panel-header>
                <mat-panel-title>Registros de trabajo</mat-panel-title>
                <mat-panel-description
                  >{{ ctx.evidences.working_times.length }} registro{{
                    ctx.evidences.working_times.length === 1 ? '' : 's'
                  }}</mat-panel-description
                >
              </mat-expansion-panel-header>

              @if (ctx.evidences.working_times.length === 0) {
                <p class="empty-state-inline">No se han encontrado registros de trabajo relacionados con este desplazamiento.</p>
              } @else {
                <ul class="evidence-list">
                  @for (evidence of ctx.evidences.working_times; track evidence.id) {
                    <li class="evidence-item">
                      <img
                        class="evidence-avatar"
                        [src]="evidence.user.profile_picture_url || 'assets/img/default_profile_picture.png'"
                        [alt]="evidence.user.full_name"
                        onerror="this.src='assets/img/default_profile_picture.png'"
                      />
                      <div class="evidence-item-info">
                        <p class="evidence-item-title">{{ evidence.user.full_name }} · {{ evidence.started_at | date: 'dd/MM HH:mm' }}</p>
                        <p class="evidence-item-detail">
                          Duración: {{ evidence.duration_in_seconds | secondsToStringTime }}
                          @if (evidence.location_name) {
                            · {{ evidence.location_name }}
                          }
                        </p>
                        <p class="evidence-item-detail">
                          Al origen: {{ formatDistanceMeters(evidence.distance_to_origin_meters) }} · Al destino:
                          {{ formatDistanceMeters(evidence.distance_to_destination_meters) }}
                        </p>
                      </div>
                      <span [class]="'relation-badge ' + getEvidenceRelationVisual(evidence.relation).cssClass">
                        <mat-icon class="icon-small" aria-hidden="true">{{ getEvidenceRelationVisual(evidence.relation).icon }}</mat-icon>
                        {{ getEvidenceRelationVisual(evidence.relation).label }}
                      </span>
                    </li>
                  }
                </ul>
              }
            </mat-expansion-panel>

            <mat-expansion-panel>
              <mat-expansion-panel-header>
                <mat-panel-title>Actas de reunión con el cliente</mat-panel-title>
                <mat-panel-description
                  >{{ ctx.evidences.meetings.length }} reunión{{ ctx.evidences.meetings.length === 1 ? '' : 'es' }}</mat-panel-description
                >
              </mat-expansion-panel-header>

              @if (ctx.evidences.meetings.length === 0) {
                <p class="empty-state-inline">No se han encontrado actas de reunión con el cliente ese día.</p>
              } @else {
                <ul class="evidence-list">
                  @for (meeting of ctx.evidences.meetings; track meeting.employee_calendar_register_id) {
                    <li class="evidence-item evidence-item--simple">
                      <div class="evidence-item-info">
                        <p class="evidence-item-title">{{ meeting.title }}</p>
                        <p class="evidence-item-detail">
                          {{ meeting.from_date | date: 'dd/MM HH:mm' }} – {{ meeting.to_date | date: 'HH:mm' }} ·
                          {{ meeting.attendees_count }} asistente{{ meeting.attendees_count === 1 ? '' : 's' }}
                        </p>
                      </div>
                      <span class="minutes-badge" [class.minutes-badge--yes]="meeting.has_minutes">
                        <mat-icon class="icon-small" aria-hidden="true">{{
                          meeting.has_minutes ? 'description' : 'remove_circle_outline'
                        }}</mat-icon>
                        {{ meeting.has_minutes ? 'Con acta' : 'Sin acta' }}
                      </span>
                    </li>
                  }
                </ul>
              }
            </mat-expansion-panel>

            <mat-expansion-panel>
              <mat-expansion-panel-header>
                <mat-panel-title>Asistentes y votaciones</mat-panel-title>
                <mat-panel-description
                  >{{ ctx.evidences.meeting_attendances.length }} respuesta{{
                    ctx.evidences.meeting_attendances.length === 1 ? '' : 's'
                  }}</mat-panel-description
                >
              </mat-expansion-panel-header>

              @if (ctx.evidences.meeting_attendances.length === 0) {
                <p class="empty-state-inline">No hay respuestas de asistencia registradas para este desplazamiento.</p>
              } @else {
                <ul class="evidence-list">
                  @for (attendance of ctx.evidences.meeting_attendances; track attendance.id) {
                    <li class="evidence-item evidence-item--simple">
                      <div class="evidence-item-info">
                        <p class="evidence-item-title">
                          @if (attendance.is_self_reported) {
                            {{ attendance.answering_user.full_name }} (sobre sí mismo)
                          } @else {
                            {{ attendance.answering_user.full_name }} → sobre {{ attendance.target_user.full_name }}
                          }
                        </p>
                        <p class="evidence-item-detail">
                          Asistencia: {{ MeetingAttendanceStatusLabels[attendance.attendance_status] }}
                          @if (attendance.trip_transport_type) {
                            · {{ TripTransportTypeLabels[attendance.trip_transport_type] }}
                          }
                        </p>
                      </div>
                      @if (attendance.is_target_user_driver) {
                        <span class="driver-flag-badge">
                          <mat-icon class="icon-small" aria-hidden="true">directions_car</mat-icon>
                          Señalado como conductor
                        </span>
                      }
                    </li>
                  }
                </ul>
              }
            </mat-expansion-panel>

            <mat-expansion-panel>
              <mat-expansion-panel-header>
                <mat-panel-title>Ubicaciones</mat-panel-title>
                <mat-panel-description
                  >{{ ctx.evidences.locations.length }} ubicaci{{
                    ctx.evidences.locations.length === 1 ? 'ón' : 'ones'
                  }}</mat-panel-description
                >
              </mat-expansion-panel-header>

              @if (ctx.evidences.locations.length === 0) {
                <p class="empty-state-inline">No hay ubicaciones relevantes registradas para este desplazamiento.</p>
              } @else {
                <ul class="evidence-list">
                  @for (location of ctx.evidences.locations; track location.label + location.name) {
                    <li class="evidence-item evidence-item--simple">
                      <div class="evidence-item-info">
                        <p class="evidence-item-title">{{ location.label }}</p>
                        <p class="evidence-item-detail">{{ location.name }}</p>
                        <p class="evidence-item-detail">
                          @if (location.kind !== DrivingRequestLocationEvidenceKindEnum.ORIGIN) {
                            Al origen: {{ formatDistanceMeters(location.distance_to_origin_meters) }}
                          }
                          @if (
                            location.kind !== DrivingRequestLocationEvidenceKindEnum.ORIGIN &&
                            location.kind !== DrivingRequestLocationEvidenceKindEnum.DESTINATION
                          ) {
                            ·
                          }
                          @if (location.kind !== DrivingRequestLocationEvidenceKindEnum.DESTINATION) {
                            Al destino: {{ formatDistanceMeters(location.distance_to_destination_meters) }}
                          }
                        </p>
                      </div>
                    </li>
                  }
                </ul>
              }
            </mat-expansion-panel>
          </mat-accordion>
        </section>

        <section class="final-decision" aria-label="Decisión final">
          @if (submit_error(); as error_message) {
            <p class="field-error" role="alert">
              <mat-icon class="icon-small" aria-hidden="true">error_outline</mat-icon>
              {{ error_message }}
            </p>
          }
          @if (selected_candidate(); as selected) {
            <p class="selected-driver-summary">
              <mat-icon class="icon-small" aria-hidden="true">check_circle</mat-icon>
              Conductor seleccionado: <strong>{{ selected.full_name }}</strong>
            </p>
          }
        </section>
      }
    }
  }
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button
    mat-stroked-button
    color="warn"
    type="button"
    [disabled]="actions_disabled()"
    (click)="resolve(EmployeeRequestResponsibleForAnswerStatusEnum.REJECTED)"
  >
    Rechazar
  </button>
  <button
    mat-flat-button
    color="accent"
    type="button"
    [disabled]="!can_accept()"
    (click)="resolve(EmployeeRequestResponsibleForAnswerStatusEnum.ACCEPTED)"
  >
    @if (submitting()) {
      <mat-icon class="icon-small spin-icon" aria-hidden="true">autorenew</mat-icon>
    }
    Aceptar
  </button>
</mat-dialog-actions>
